import "@/components/ui/atoms/categories/data-display.css";
import "@/components/home/figure-caption.css";
import { memo, type ReactNode } from "react";

/**
 * FigureCaption — the ruled caption a marketing figure carries: "Fig. N" set
 * as a mono overline over a serif lead, then the muted detail beneath.
 *
 * Seven figures across plates, one-system and how-it-works hand-wrote the
 * same three spans under three block prefixes. The twelve rules were one
 * rule twice over: the only difference was a `max-width` on the detail span
 * that plates omitted, and it is inert there — the widest plates caption
 * column is about 35rem against a 44rem cap. The shared rule keeps it.
 *
 * Figures whose caption is a single line write "Fig. N · one sentence" into
 * one element with no spans inside. That is a different shape, and it is
 * FigureCaptionLine below — not a variant of this component.
 *
 * The numbers are sequential across the whole page and stay hand-written
 * here; nothing in this component orders them.
 */
export const FigureCaption = memo(function FigureCaption({
  figure,
  lead,
  text,
}: {
  figure: string;
  lead: ReactNode;
  text: ReactNode;
}) {
  return (
    <figcaption className="dw-home__caption" data-component="FigureCaption" data-location="src/components/home/figure-caption.tsx">
      <span className="dw-home__fig-label dw-text-caption dw-tabular-nums" data-tone="muted">Fig. {figure}</span>
      <span className="dw-text-lead" data-tone="foreground">{lead}</span>
      <span className="dw-home__caption-text dw-text-body" data-tone="muted">{text}</span>
    </figcaption>
  );
});

/**
 * FigureCaptionLine — the other caption shape: one line, "Fig. N · one
 * sentence", ruled off the figure it heads and set as a mono overline. Nothing
 * inside it but the line.
 *
 * Nine captions — clerk ×3, ledger ×3, the game, and the two features modules
 * — wrote this under four block prefixes. Three of the four rules were
 * byte-identical. The space under the line is its figure's gap, so the
 * ledger's tighter step lives on the ledger's figures, not here.
 *
 * The crawl's caption shares a baseline row with the music button, so it
 * draws no rule of its own: `variant="bare"`, stamped as `data-variant`.
 *
 * The line arrives whole, as children, not as a figure number. One site heads
 * a table ("Table 1 · …") and two compose the string upstream, so there is no
 * "Fig. {n}" here for this component to assemble.
 */
export const FigureCaptionLine = memo(function FigureCaptionLine({
  children,
  variant,
}: {
  children: ReactNode;
  variant?: "bare";
}) {
  return (
    <figcaption className="dw-home__figcaption dw-eyebrow dw-tabular-nums" data-variant={variant} data-component="FigureCaptionLine" data-location="src/components/home/figure-caption.tsx">
      {children}
    </figcaption>
  );
});
